import React, { useEffect, useMemo, useState } from "react";
import { useComisionLedger } from "../../hooks/useComisionLedger";
import { useClients, useAfianzadoras } from "../../contexts/DataContext";
import { Heading } from "../../components/heading";
import { Text } from "../../components/text";
import TablePager from "../../components/TablePager";
import { mapAfianzadoraName } from '../../utils/afianzadoras';

const FILAS_POR_PAGINA = 10;

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

// Same display mapping the other afianzadora reportes use.

const GROUPS = [
  { id: "afianzadora", label: "Afianzadora" },
  { id: "cliente", label: "Cliente" },
];

/**
 * LiquidacionComisiones — the settlement view over the comisión ledger.
 *
 * Sums movements (emision/adicional/sustituye positive, cargo negative) per
 * afianzadora or cliente, split into cobrada vs pendiente, with the net balance.
 * A negative balance means the agent owes that afianzadora (it nets the debt
 * against future comisiones). This dimension does not exist in the on-the-fly
 * production reportes — it is what the ledger adds.
 */
export function LiquidacionComisiones({ selectedYear }) {
  const { movements, loading } = useComisionLedger();
  const { clientsById } = useClients();
  const { afianzadorasList } = useAfianzadoras();
  const [groupBy, setGroupBy] = useState("afianzadora");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [groupBy, selectedYear]);

  const inYear = useMemo(
    () =>
      movements.filter((m) => {
        const ms = Number(m.fecha) || 0;
        return ms && new Date(ms).getFullYear() === selectedYear;
      }),
    [movements, selectedYear]
  );

  const keyFor = (m) => {
    if (groupBy === "cliente") {
      if (m.clienteId) return clientsById[m.clienteId]?.nombreEmpresa || m.clienteId;
      if (m.convenioId) return m.convenioNombre || `Convenio ${m.convenioId}`;
      return "Sin cliente";
    }
    return mapAfianzadoraName(m.afianzadora, afianzadorasList);
  };

  // Four money figures, kept apart on purpose:
  //   cobrada   — comisión the afianzadora already paid
  //   porCobrar — comisión earned and still owed TO the agent
  //   adeudo    — comisión the agent must give BACK, because a fianza that had
  //               already been paid out was anulada/sustituida/reducida. This is
  //               a liability, not a negative receivable: netting it into
  //               "pendiente" would read as money still coming in.
  // A reversal of a comisión that was never collected is 'neutralizada' — it
  // cancels out in `neto` and owes nothing, so it belongs in neither bucket.
  const { rows, totals } = useMemo(() => {
    const blank = (key) => ({
      key, bruta: 0, cargos: 0, neto: 0, cobrada: 0, porCobrar: 0, adeudo: 0, count: 0,
      // Convenio split bookkeeping: participación = what the split cost/paid
      // (gross − net); sinPct/estimados need attention before the numbers are
      // final (pct-pending sums as an invisible 0 without this).
      participacion: 0, sinPct: 0, estimados: 0,
    });
    const map = new Map();
    const t = blank(null);
    inYear.forEach((m) => {
      const key = keyFor(m);
      if (!map.has(key)) map.set(key, blank(key));
      const g = map.get(key);
      if (m.comisionMonto == null) { g.sinPct += 1; t.sinPct += 1; }
      else if (m.pctEstimado) { g.estimados += 1; t.estimados += 1; }
      if (m.splitRol && m.comisionBase != null && m.comisionMonto != null) {
        const part = Number(m.comisionBase) - Number(m.comisionMonto);
        g.participacion += part; t.participacion += part;
      }
      const monto = Number(m.comisionMonto) || 0;
      if (monto >= 0) { g.bruta += monto; t.bruta += monto; }
      else { g.cargos += monto; t.cargos += monto; }
      g.neto += monto; t.neto += monto;

      switch (m.estadoCobro) {
        case "cobrada":
          g.cobrada += monto; t.cobrada += monto;
          break;
        case "pendiente":
          g.porCobrar += monto; t.porCobrar += monto;
          break;
        case "adeudo":
          // Stored as a negative movement; shown as a positive debt.
          g.adeudo += Math.abs(monto); t.adeudo += Math.abs(monto);
          break;
        default:
          // neutralizada / adeudo_liquidado: already settled or never owed.
          break;
      }
      g.count += 1; t.count += 1;
    });
    // One signed saldo per group: what remains to settle with that afianzadora.
    // Positive → they owe the agent (por cobrar); negative → the agent owes
    // them (adeudo, netted from future liquidaciones). One number, one sign.
    map.forEach((g) => { g.saldo = g.porCobrar - g.adeudo; });
    t.saldo = t.porCobrar - t.adeudo;
    const rowsArr = Array.from(map.values()).sort((a, b) => b.neto - a.neto);
    return { rows: rowsArr, totals: t };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inYear, groupBy, clientsById, afianzadorasList]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">Liquidación de comisiones</Heading>
          <Text className="text-sm text-azul-surelia/70">
            Comisión cobrada y saldo por liquidar con cada {groupBy}. {selectedYear}.
          </Text>
        </div>
        <div className="inline-flex rounded-lg border border-azul-surelia/20 overflow-hidden" data-pdf-hide>
          {GROUPS.map((g) => (
            <button
              key={g.id}
              onClick={() => setGroupBy(g.id)}
              className={`px-3 py-1.5 text-sm ${groupBy === g.id ? "bg-azul-surelia text-white" : "bg-white text-azul-surelia"}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-zinc-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Neto</div>
          <div className="text-lg font-bold text-azul-surelia">{fmtMXN(totals.neto)}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-600">Cobrada</div>
          <div className="text-lg font-bold text-emerald-800">{fmtMXN(totals.cobrada)}</div>
        </div>
        <div
          className={`rounded-xl border p-3 ${
            totals.saldo < 0 ? "border-red-300 bg-red-50" : "border-amber-200 bg-amber-50"
          }`}
          title="Lo que falta por liquidar: positivo, la afianzadora se lo debe al agente; negativo, el agente lo debe (se neta de futuras liquidaciones)."
        >
          <div className={`text-[10px] uppercase tracking-wide ${totals.saldo < 0 ? "text-red-600" : "text-amber-600"}`}>
            Saldo por liquidar
          </div>
          <div className={`text-lg font-bold ${totals.saldo < 0 ? "text-red-700" : "text-amber-800"}`}>
            {fmtMXN(totals.saldo)}
          </div>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-red-600">Cargos</div>
          <div className="text-lg font-bold text-red-700">{fmtMXN(totals.cargos)}</div>
        </div>
      </div>

      {/* Convenio-split attention line: pct-pending movements sum as 0, so the
          totals above are understated until their % is defined; estimados count
          but await confirmation against the real fiado-agent %. */}
      {(totals.sinPct > 0 || totals.estimados > 0 || totals.participacion > 0) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex flex-wrap gap-x-5 gap-y-1">
          {totals.sinPct > 0 && (
            <span title="Movimientos de convenio sin % de comisión: no suman al neto. Define el % desde la fianza.">
              Sin % definido: <span className="font-semibold">{totals.sinPct} mov.</span>
            </span>
          )}
          {totals.estimados > 0 && (
            <span title="Comisión estimada con la tabla de comisiones por defecto; confírmala al ver el % real.">
              % estimado: <span className="font-semibold">{totals.estimados} mov.</span>
            </span>
          )}
          {totals.participacion > 0 && (
            <span title="Parte de la comisión bruta que corresponde a la otra parte del convenio (no es tuya).">
              Participación de convenios: <span className="font-semibold">{fmtMXN(totals.participacion)}</span>
            </span>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          No hay movimientos de comisión en {selectedYear}.
          <div className="text-xs text-gray-400 mt-1">
            Las fianzas anteriores requieren el respaldo del ledger (script backfill_comisiones) para aparecer aquí.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="py-2 pr-3">{GROUPS.find((g) => g.id === groupBy).label}</th>
                <th className="py-2 px-3 text-right">Bruta</th>
                <th className="py-2 px-3 text-right">Cargos</th>
                <th className="py-2 px-3 text-right">Neto</th>
                <th className="py-2 px-3 text-right">Cobrada</th>
                <th className="py-2 px-3 text-right">Saldo</th>
                <th className="py-2 pl-3 text-right">Mov.</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice((page - 1) * FILAS_POR_PAGINA, page * FILAS_POR_PAGINA).map((r) => (
                <tr key={r.key} className="border-b border-gray-100">
                  <td className="py-2 pr-3 font-medium text-gray-800">{r.key}</td>
                  <td className="py-2 px-3 text-right text-gray-700">{fmtMXN(r.bruta)}</td>
                  <td className="py-2 px-3 text-right text-red-600">{r.cargos ? fmtMXN(r.cargos) : "—"}</td>
                  <td className={`py-2 px-3 text-right font-semibold ${r.neto < 0 ? "text-red-600" : "text-azul-surelia"}`}>
                    {fmtMXN(r.neto)}
                  </td>
                  <td className="py-2 px-3 text-right text-emerald-700">{fmtMXN(r.cobrada)}</td>
                  <td className={`py-2 px-3 text-right font-semibold ${r.saldo < 0 ? "text-red-700" : "text-amber-700"}`}>
                    {fmtMXN(r.saldo)}
                  </td>
                  <td className="py-2 pl-3 text-right text-gray-400">{r.count}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-semibold text-gray-800">
                <td className="py-2 pr-3">Total</td>
                <td className="py-2 px-3 text-right">{fmtMXN(totals.bruta)}</td>
                <td className="py-2 px-3 text-right text-red-600">{fmtMXN(totals.cargos)}</td>
                <td className={`py-2 px-3 text-right ${totals.neto < 0 ? "text-red-600" : "text-azul-surelia"}`}>{fmtMXN(totals.neto)}</td>
                <td className="py-2 px-3 text-right text-emerald-700">{fmtMXN(totals.cobrada)}</td>
                <td className={`py-2 px-3 text-right ${totals.saldo < 0 ? "text-red-700" : "text-amber-700"}`}>
                  {fmtMXN(totals.saldo)}
                </td>
                <td className="py-2 pl-3 text-right text-gray-400">{totals.count}</td>
              </tr>
            </tfoot>
          </table>
          <TablePager
            total={rows.length}
            page={page}
            onPage={setPage}
            perPage={FILAS_POR_PAGINA}
            itemLabel="filas"
          />
        </div>
      )}

      <p className="text-[11px] text-gray-400">
        Saldo positivo: la afianzadora se lo debe al agente. Saldo negativo: el agente lo debe
        y la afianzadora lo descuenta de comisiones futuras.
      </p>
    </div>
  );
}

export default LiquidacionComisiones;
